/**
 * Client-side garment segmentation using SegFormer B0 via ONNX Runtime Web.
 *
 * Model: Xenova/segformer_b0_clothes (INT8 quantized, 4.2 MB)
 * Runs entirely in the browser - no server round-trip needed.
 *
 * Label map (18 classes):
 *   0  Background    6  Pants        12 Left-leg
 *   1  Hat           7  Dress        13 Right-leg
 *   2  Hair          8  Belt         14 Left-arm
 *   3  Sunglasses    9  Left-shoe    15 Right-arm
 *   4  Upper-clothes 10 Right-shoe   16 Bag
 *   5  Skirt         11 Face         17 Scarf
 */

// Dynamic import - avoids crashing if @xenova/transformers isn't installed yet.
// The library is loaded lazily on first segmentation call.
type TransformersModule = typeof import('@xenova/transformers');
let _transformers: TransformersModule | null = null;

async function getTransformers(): Promise<TransformersModule> {
  if (_transformers) return _transformers;
  _transformers = await import('@xenova/transformers');
  return _transformers;
}

// ---------------------------------------------------------------------------
// Configuration
// ---------------------------------------------------------------------------

// Fine-tuned SegFormer B0 with knowledge distillation from B2
// val_miou: 0.507 (vs 0.111 original), garment_miou: 0.410
// INT8 quantized, ~4.2MB - runs in-browser via ONNX Runtime Web
const MODEL_ID = 'almogtavor/vestir-segformer-b0-clothes';

/** SegFormer label index → (category, subCategory) for garment labels only. */
const GARMENT_LABEL_MAP: Record<number, { category: string; subCategory: string }> = {
  1:  { category: 'accessories', subCategory: 'hat' },
  3:  { category: 'accessories', subCategory: 'sunglasses' },
  4:  { category: 'tops',        subCategory: 'upper-clothes' },
  5:  { category: 'bottoms',     subCategory: 'skirt' },
  6:  { category: 'bottoms',     subCategory: 'pants' },
  7:  { category: 'dresses',     subCategory: 'dress' },
  8:  { category: 'accessories', subCategory: 'belt' },
  9:  { category: 'shoes',       subCategory: 'shoe' },
  10: { category: 'shoes',       subCategory: 'shoe' },
  16: { category: 'accessories', subCategory: 'bag' },
  17: { category: 'accessories', subCategory: 'scarf' },
};

const GARMENT_LABEL_IDS = new Set(Object.keys(GARMENT_LABEL_MAP).map(Number));

const LABEL_NAMES: Record<number, string> = {
  0: 'Background', 1: 'Hat', 2: 'Hair', 3: 'Sunglasses',
  4: 'Upper-clothes', 5: 'Skirt', 6: 'Pants', 7: 'Dress',
  8: 'Belt', 9: 'Left-shoe', 10: 'Right-shoe', 11: 'Face',
  12: 'Left-leg', 13: 'Right-leg', 14: 'Left-arm', 15: 'Right-arm',
  16: 'Bag', 17: 'Scarf',
};

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface ClientDetectedGarment {
  bbox: [number, number, number, number]; // [x1, y1, x2, y2]
  confidence: number;
  segmentImageBase64: string; // transparent PNG, base64
  category: string;
  subCategory: string;
  labelId: number;
  labelName: string;
  primaryColors: Array<{ hex: string; rgb: number[]; percentage: number }>;
  areaPixels: number;
}

export interface ClientSegmentationResult {
  garments: ClientDetectedGarment[];
  processingTimeMs: number;
  modelId: string;
}

// ---------------------------------------------------------------------------
// Singleton pipeline
// ---------------------------------------------------------------------------

let _pipeline: any | null = null;
let _pipelineLoading: Promise<any> | null = null;

/**
 * Warm up the segmentation pipeline (downloads + compiles the model).
 * Call this early (e.g. on page load) so the first segmentation is fast.
 */
export async function warmUpSegmentation(
  onProgress?: (progress: number) => void,
): Promise<void> {
  await getOrCreatePipeline(onProgress);
}

async function getOrCreatePipeline(
  onProgress?: (progress: number) => void,
): Promise<any> {
  if (_pipeline) return _pipeline;

  if (_pipelineLoading) return _pipelineLoading;

  _pipelineLoading = (async () => {
    const T = await getTransformers();

    // Use WASM backend (works on all browsers incl. iPhone Safari)
    T.env.backends.onnx.wasm.numThreads = 1; // Safari doesn't support SharedArrayBuffer

    const p = await T.pipeline('image-segmentation', MODEL_ID, {
      quantized: true, // Use the 4.2 MB INT8 model
      progress_callback: onProgress
        ? (data: { progress?: number }) => {
            if (data.progress != null) onProgress(data.progress / 100);
          }
        : undefined,
    });

    _pipeline = p;
    return _pipeline;
  })();

  return _pipelineLoading;
}

// ---------------------------------------------------------------------------
// Main API
// ---------------------------------------------------------------------------

/**
 * Segment garments from an image entirely on the client.
 *
 * @param imageSource  A File, Blob, HTMLImageElement, or image URL.
 * @param onProgress   Optional progress callback (0-1).
 * @returns            Detected garments with transparent PNG segments.
 */
export async function segmentGarmentsClient(
  imageSource: File | Blob | HTMLImageElement | string,
  onProgress?: (progress: number, stage: string) => void,
): Promise<ClientSegmentationResult> {
  const t0 = performance.now();

  onProgress?.(0.05, 'Loading model…');
  const seg = await getOrCreatePipeline();

  onProgress?.(0.2, 'Preparing image…');

  // Pre-process: resize large images and convert HEIC/non-standard formats
  // to JPEG data URLs via canvas. This ensures compatibility and speed.
  const imageUrl = await prepareImageForSegmentation(imageSource);

  // Load as RawImage for direct pixel access
  const T = await getTransformers();
  const rawImage = await T.RawImage.fromURL(imageUrl);
  const { width: origW, height: origH } = rawImage;

  onProgress?.(0.3, 'Running segmentation…');

  // Run the pipeline - returns per-label masks
  const results = await seg(imageUrl);

  onProgress?.(0.7, 'Extracting garments…');

  // Build per-pixel label map from pipeline output
  const segMap = new Uint8Array(origW * origH);
  for (const result of results) {
    const labelName = (result as any).label as string;
    const labelId = Object.entries(LABEL_NAMES).find(([, n]) => n === labelName)?.[0];
    if (labelId == null) continue;
    const lid = Number(labelId);
    const mask = (result as any).mask;
    if (!mask) continue;

    // mask is a RawImage; its data is a Uint8Array/Uint8ClampedArray
    const maskData = mask.data as Uint8Array;
    const maskW = mask.width as number;
    const maskH = mask.height as number;

    // If mask size matches original, use directly; otherwise scale
    // Use a lower threshold (64) to capture more of the segmented region.
    // The model outputs soft masks; 127 was too aggressive and missed edges.
    const maskThreshold = 64;
    if (maskW === origW && maskH === origH) {
      for (let i = 0; i < maskData.length; i++) {
        if (maskData[i] > maskThreshold) segMap[i] = lid;
      }
    } else {
      // Simple nearest-neighbor scale
      for (let y = 0; y < origH; y++) {
        for (let x = 0; x < origW; x++) {
          const srcX = Math.floor((x / origW) * maskW);
          const srcY = Math.floor((y / origH) * maskH);
          if (maskData[srcY * maskW + srcX] > maskThreshold) {
            segMap[y * origW + x] = lid;
          }
        }
      }
    }
  }

  // Extract garments from label map
  const garments: ClientDetectedGarment[] = [];

  // Merge left-shoe (9) and right-shoe (10)
  const uniqueLabels = new Set(segMap);
  const garmentLabels = [...uniqueLabels].filter((l) => GARMENT_LABEL_IDS.has(l));

  // Merge shoes
  const hasShoes = garmentLabels.includes(9) || garmentLabels.includes(10);
  const processLabels = garmentLabels.filter((l) => l !== 10); // process 9 as merged shoe
  if (hasShoes && !processLabels.includes(9)) processLabels.push(9);

  // Get original pixel data
  const pixelData = rawImage.data as Uint8Array; // RGB or RGBA
  const channels = rawImage.channels as number;

  for (const labelId of processLabels.sort((a, b) => a - b)) {
    const mapping = GARMENT_LABEL_MAP[labelId];
    if (!mapping) continue;

    // Build binary mask
    const mask = new Uint8Array(origW * origH);
    for (let i = 0; i < segMap.length; i++) {
      if (labelId === 9) {
        // Merge shoes
        if (segMap[i] === 9 || segMap[i] === 10) mask[i] = 255;
      } else {
        if (segMap[i] === labelId) mask[i] = 255;
      }
    }

    // Count pixels, skip tiny regions (0.1% of image area minimum)
    const pixelCount = mask.reduce((sum, v) => sum + (v > 0 ? 1 : 0), 0);
    if (pixelCount < origW * origH * 0.001) continue;

    // Bounding box
    let x1 = origW, y1 = origH, x2 = 0, y2 = 0;
    for (let y = 0; y < origH; y++) {
      for (let x = 0; x < origW; x++) {
        if (mask[y * origW + x] > 0) {
          if (x < x1) x1 = x;
          if (x > x2) x2 = x;
          if (y < y1) y1 = y;
          if (y > y2) y2 = y;
        }
      }
    }

    // Create transparent PNG segment
    const cropW = x2 - x1 + 1;
    const cropH = y2 - y1 + 1;
    const segmentCanvas = document.createElement('canvas');
    segmentCanvas.width = cropW;
    segmentCanvas.height = cropH;
    const ctx = segmentCanvas.getContext('2d')!;
    const imageData = ctx.createImageData(cropW, cropH);

    for (let dy = 0; dy < cropH; dy++) {
      for (let dx = 0; dx < cropW; dx++) {
        const srcX = x1 + dx;
        const srcY = y1 + dy;
        const srcIdx = (srcY * origW + srcX) * channels;
        const dstIdx = (dy * cropW + dx) * 4;

        imageData.data[dstIdx] = pixelData[srcIdx];         // R
        imageData.data[dstIdx + 1] = pixelData[srcIdx + 1]; // G
        imageData.data[dstIdx + 2] = pixelData[srcIdx + 2]; // B
        imageData.data[dstIdx + 3] = mask[srcY * origW + srcX]; // A from mask
      }
    }

    ctx.putImageData(imageData, 0, 0);
    const segmentBase64 = segmentCanvas.toDataURL('image/png').split(',')[1];

    // Extract dominant colors (sample masked pixels)
    const colors = extractDominantColors(pixelData, mask, origW, origH, channels);

    // Confidence from pixel coverage relative to bounding box
    const bboxArea = cropW * cropH;
    const confidence = Math.min(0.99, Math.round((pixelCount / bboxArea) * 100) / 100);

    garments.push({
      bbox: [x1, y1, x2, y2],
      confidence,
      segmentImageBase64: segmentBase64,
      category: mapping.category,
      subCategory: mapping.subCategory,
      labelId,
      labelName: LABEL_NAMES[labelId] ?? 'Unknown',
      primaryColors: colors,
      areaPixels: pixelCount,
    });
  }

  const processingTimeMs = Math.round(performance.now() - t0);

  onProgress?.(1.0, 'Done');

  return {
    garments,
    processingTimeMs,
    modelId: MODEL_ID,
  };
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/**
 * Resize large images and convert any format (including HEIC) to a JPEG
 * data URL via an offscreen canvas. Max dimension is 1024px.
 */
async function prepareImageForSegmentation(
  source: File | Blob | HTMLImageElement | string,
): Promise<string> {
  const MAX_DIM = 1024;

  // Load into an HTMLImageElement
  let img: HTMLImageElement;
  if (source instanceof HTMLImageElement) {
    img = source;
  } else {
    const url =
      typeof source === 'string'
        ? source
        : URL.createObjectURL(source as Blob);
    img = await new Promise<HTMLImageElement>((resolve, reject) => {
      const el = new Image();
      el.onload = () => resolve(el);
      el.onerror = () => reject(new Error('Failed to load image'));
      el.src = url;
    });
    if (typeof source !== 'string') URL.revokeObjectURL(url);
  }

  // Compute target dimensions
  let { naturalWidth: w, naturalHeight: h } = img;
  if (w === 0 || h === 0) {
    w = img.width;
    h = img.height;
  }
  const scale = Math.min(1, MAX_DIM / Math.max(w, h));
  const newW = Math.round(w * scale);
  const newH = Math.round(h * scale);

  // Draw onto canvas → JPEG data URL (universally supported, fast)
  const canvas = document.createElement('canvas');
  canvas.width = newW;
  canvas.height = newH;
  const ctx = canvas.getContext('2d')!;
  ctx.drawImage(img, 0, 0, newW, newH);
  return canvas.toDataURL('image/jpeg', 0.9);
}

function blobToDataUrl(blob: Blob | File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result as string);
    reader.onerror = reject;
    reader.readAsDataURL(blob);
  });
}

function extractDominantColors(
  pixelData: Uint8Array,
  mask: Uint8Array,
  w: number,
  h: number,
  channels: number,
  maxSamples = 2000,
): Array<{ hex: string; rgb: number[]; percentage: number }> {
  // Collect masked pixel colors
  const samples: number[][] = [];
  const step = Math.max(1, Math.floor((w * h) / maxSamples));

  for (let i = 0; i < w * h; i += step) {
    if (mask[i] > 0) {
      const idx = i * channels;
      samples.push([pixelData[idx], pixelData[idx + 1], pixelData[idx + 2]]);
    }
  }

  if (samples.length === 0) {
    return [{ hex: '#808080', rgb: [128, 128, 128], percentage: 100 }];
  }

  // Simple k-means with k=3
  const k = Math.min(3, samples.length);
  let centroids = samples.slice(0, k).map((s) => [...s]);

  for (let iter = 0; iter < 10; iter++) {
    const clusters: number[][][] = Array.from({ length: k }, () => []);
    for (const sample of samples) {
      let minDist = Infinity;
      let minIdx = 0;
      for (let c = 0; c < k; c++) {
        const dist =
          (sample[0] - centroids[c][0]) ** 2 +
          (sample[1] - centroids[c][1]) ** 2 +
          (sample[2] - centroids[c][2]) ** 2;
        if (dist < minDist) {
          minDist = dist;
          minIdx = c;
        }
      }
      clusters[minIdx].push(sample);
    }

    for (let c = 0; c < k; c++) {
      if (clusters[c].length === 0) continue;
      centroids[c] = [
        Math.round(clusters[c].reduce((s, p) => s + p[0], 0) / clusters[c].length),
        Math.round(clusters[c].reduce((s, p) => s + p[1], 0) / clusters[c].length),
        Math.round(clusters[c].reduce((s, p) => s + p[2], 0) / clusters[c].length),
      ];
    }
  }

  // Assign final labels
  const counts = new Array(k).fill(0);
  for (const sample of samples) {
    let minDist = Infinity;
    let minIdx = 0;
    for (let c = 0; c < k; c++) {
      const dist =
        (sample[0] - centroids[c][0]) ** 2 +
        (sample[1] - centroids[c][1]) ** 2 +
        (sample[2] - centroids[c][2]) ** 2;
      if (dist < minDist) {
        minDist = dist;
        minIdx = c;
      }
    }
    counts[minIdx]++;
  }

  const total = counts.reduce((a, b) => a + b, 0);
  return centroids
    .map((c, i) => ({
      hex: `#${c.map((v) => v.toString(16).padStart(2, '0')).join('')}`,
      rgb: c,
      percentage: Math.round((counts[i] / total) * 1000) / 10,
    }))
    .sort((a, b) => b.percentage - a.percentage);
}
